import CodeBlock from '@tiptap/extension-code-block';
import { DragHandle } from '@tiptap/extension-drag-handle-react';
import Image from '@tiptap/extension-image';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import type { Node } from '@tiptap/pm/model';
import {
  type Editor,
  EditorContent,
  type JSONContent,
  NodeViewContent,
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  useEditor,
  useEditorState,
} from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Code,
  CodeXml,
  GripVertical,
  Heading1,
  Heading2,
  Heading3,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListChecks,
  ListOrdered,
  Minus,
  Plus,
  Quote,
  Strikethrough,
  Table2,
  Type,
  Underline,
} from 'lucide-react';
import {
  type ComponentType,
  type RefObject,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { blockText } from '../../shared/export';
import { type Asset, imageAssetId, resolveImages } from '../../shared/media';
import { newTable } from '../../shared/model';

import {
  type BlockCommand,
  type BlockKind,
  matchCommands,
  turnIntoKinds,
} from '../lib/block-commands';
import {
  atDocumentStart,
  BlockPlaceholders,
  moveSelectedBlock,
  NotionKeys,
  slashRange,
} from '../lib/block-editing';
import { workspace } from '../lib/engine';
import { moveBlock } from '../lib/move-block';
import { TypedTableExtension } from './TypedTable';
import { Button, ControlPopover, IconButton, Input, Select } from './ui';

function CodeView({ node, updateAttributes, editor }: NodeViewProps) {
  const locked = useEditorState({ editor, selector: ({ editor }) => !editor.isEditable });
  const [copied, setCopied] = useState(false);

  return (
    <NodeViewWrapper className="code-block">
      <div className="code-header" contentEditable={false}>
        <Input
          disabled={locked}
          variant="inline"
          aria-label="Code language"
          value={node.attrs.language ?? ''}
          placeholder="plain text"
          onChange={e => updateAttributes({ language: e.target.value })}
        />
        <Button
          size="compact"
          variant="ghost"
          onClick={() => {
            navigator.clipboard
              .writeText(node.textContent)
              .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1800);
              })
              .catch(() => setCopied(false));
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <pre>
        <NodeViewContent<'code'> as="code" />
      </pre>
    </NodeViewWrapper>
  );
}

function ImageView({ node }: NodeViewProps) {
  const state = useSyncExternalStore(workspace.subscribe, workspace.snapshot);

  const id = imageAssetId(node.toJSON()),
    asset = id
      ? (state.records.find(r => r.id === id && r.kind === 'asset')?.value as Asset | undefined)
      : undefined;

  return (
    <NodeViewWrapper>
      {id && !asset ? (
        <p className="notice">
          This image has not finished downloading. Reconnect to complete the workspace.
        </p>
      ) : (
        <img
          src={asset?.data ?? node.attrs.src}
          alt={node.attrs.alt ?? ''}
          title={node.attrs.title ?? undefined}
        />
      )}
    </NodeViewWrapper>
  );
}

const StoredImage = Image.extend({
  addNodeView() {
    return ReactNodeViewRenderer(ImageView);
  },
});

export const editorExtensions = [
  StarterKit.configure({
    dropcursor: { color: 'var(--focus-ink)', width: 3, class: 'note-drop-cursor' },
    codeBlock: false,
    link: { openOnClick: false },
  }),
  CodeBlock.extend({
    addNodeView() {
      return ReactNodeViewRenderer(CodeView);
    },
  }),
  StoredImage.configure({ allowBase64: true }),
  TaskList,
  TaskItem.configure({ nested: true }),
  // Every empty block may carry a placeholder and the rule decides what it
  // says, so headings and list items can name themselves while a plain line
  // stays quiet until the caret is in it.
  BlockPlaceholders,
  NotionKeys,
  TypedTableExtension,
];

const kindIcons: Record<BlockCommand['id'], ComponentType<{ size?: number }>> = {
  text: Type,
  heading1: Heading1,
  heading2: Heading2,
  heading3: Heading3,
  bulleted: List,
  numbered: ListOrdered,
  todo: ListChecks,
  quote: Quote,
  code: CodeXml,
  divider: Minus,
  image: ImagePlus,
  table: Table2,
};

/** The kind the block at the selection reads as, seen from a list item as the list's kind. */
export function blockKindAt(editor: Editor): BlockKind {
  if (editor.isActive('codeBlock')) {
    return 'code';
  }

  if (editor.isActive('taskList')) {
    return 'todo';
  }

  if (editor.isActive('bulletList')) {
    return 'bulleted';
  }

  if (editor.isActive('orderedList')) {
    return 'numbered';
  }

  for (const level of [1, 2, 3] as const) {
    if (editor.isActive('heading', { level })) {
      return `heading${level}`;
    }
  }

  return editor.isActive('blockquote') ? 'quote' : 'text';
}

/**
 * Changes what the block at the selection is, keeping its words.
 *
 * The block first sheds whatever wraps it — a list item leaves its list, a
 * quoted paragraph leaves its quote — and then takes the new shape, so only
 * this block changes and a to-do among bullets splits the list around
 * itself, as it does in Notion. Tiptap joins the result onto a neighbouring
 * list of the same type on its own.
 */
export function setBlockKind(editor: Editor, kind: BlockKind) {
  if (blockKindAt(editor) === kind) {
    return;
  }

  const chain = editor.chain().focus().clearNodes();

  switch (kind) {
    case 'text':
      chain.setParagraph().run();

      return;
    case 'heading1':
    case 'heading2':
    case 'heading3':
      chain.setHeading({ level: Number(kind.slice(-1)) as 1 | 2 | 3 }).run();

      return;
    case 'bulleted':
      chain.toggleBulletList().run();

      return;
    case 'numbered':
      chain.toggleOrderedList().run();

      return;
    case 'todo':
      chain.toggleTaskList().run();

      return;
    case 'quote':
      chain.toggleBlockquote().run();

      return;
    case 'code':
      chain.toggleCodeBlock().run();

      return;
    default:
      return;
  }
}

export function NoteEditor({
  noteId,
  content,
  onChange,
  locked = false,
  editorRef,
  onExitTop,
}: {
  locked?: boolean;
  noteId: string;
  content: JSONContent;
  onChange: (content: JSONContent) => void;
  /** Handed the live editor so the page around it can move the caret in and undo. */
  editorRef?: RefObject<Editor | null>;
  /** Called when Up or Backspace leaves the top of the page, so the title can take the caret. */
  onExitTop?: () => void;
}) {
  const lockedRef = useRef(locked);

  lockedRef.current = locked;
  const contentRef = useRef(content);

  contentRef.current = content;
  const onChangeRef = useRef(onChange);

  onChangeRef.current = onChange;
  const onExitTopRef = useRef(onExitTop);

  onExitTopRef.current = onExitTop;

  const [message, setMessage] = useState(''),
    [handle, setHandle] = useState<{ node: Node | null; pos: number }>({ node: null, pos: -1 }),
    [blockMenu, setBlockMenu] = useState(false),
    [slash, setSlash] = useState<{
      from: number;
      to: number;
      query: string;
      left: number;
      top: number;
    } | null>(null),
    [slashIndex, setSlashIndex] = useState(0),
    [linkOpen, setLinkOpen] = useState(false),
    [linkHref, setLinkHref] = useState('');

  const slashRef = useRef(slash);

  slashRef.current = slash;
  const slashIndexRef = useRef(slashIndex);

  slashIndexRef.current = slashIndex;
  /** A menu the owner closed stays closed until they type another slash. */
  const dismissedSlash = useRef<number | null>(null);
  /** The current link's address, readable from the key handler the editor captured on its first render. */
  const hrefRef = useRef('');
  const imageInput = useRef<HTMLInputElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const lastSent = useRef(JSON.stringify(content));

  const editor = useEditor(
    {
      editable: !locked,
      extensions: editorExtensions,
      content,
      editorProps: {
        attributes: {
          class: 'note-prose',
          'aria-label': 'Note content',
          role: 'textbox',
          'aria-multiline': 'true',
          spellcheck: 'true',
          'aria-keyshortcuts': 'Meta+Shift+ArrowUp Meta+Shift+ArrowDown',
        },
        handleKeyDown: (view, event) => {
          if (lockedRef.current) {
            return false;
          }

          const menu = slashRef.current;

          if (menu) {
            const options = matchCommands(menu.query);

            if (options.length && event.key === 'ArrowDown') {
              setSlashIndex(index => (index + 1) % options.length);

              return true;
            }

            if (options.length && event.key === 'ArrowUp') {
              setSlashIndex(index => (index - 1 + options.length) % options.length);

              return true;
            }

            if (options.length && event.key === 'Enter') {
              const chosen = options[Math.min(slashIndexRef.current, options.length - 1)];

              setTimeout(() => runCommand(chosen, menu), 0);

              return true;
            }

            if (event.key === 'Escape') {
              dismissedSlash.current = menu.from;
              setSlash(null);

              return true;
            }
          }

          if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
            if (!view.state.selection.empty) {
              openLink();

              return true;
            }

            return false;
          }

          if (
            (event.key === 'ArrowUp' ||
              (event.key === 'Backspace' && view.state.doc.textContent === '')) &&
            atDocumentStart(view.state) &&
            onExitTopRef.current
          ) {
            onExitTopRef.current();

            return true;
          }

          return false;
        },
        handlePaste: (_view, event) => {
          if (lockedRef.current) {
            return false;
          }

          const image = [...(event.clipboardData?.files ?? [])].find(f =>
            f.type.startsWith('image/'),
          );

          if (!image) {
            return false;
          }

          void insertImage(image);

          return true;
        },
      },
      onUpdate: ({ editor }) => {
        if (lockedRef.current) {
          return;
        }

        const next = editor.getJSON();

        lastSent.current = JSON.stringify(next);
        onChangeRef.current(next);
      },
      onBlur: ({ editor }) => {
        if (
          !workspace.snapshot().saving &&
          !workspace.snapshot().error &&
          JSON.stringify(contentRef.current) !== lastSent.current
        ) {
          editor.commands.setContent(contentRef.current, { emitUpdate: false });
          lastSent.current = JSON.stringify(contentRef.current);
        }
      },
      onTransaction: ({ editor }) => trackSlash(editor),
    },
    [noteId],
  );

  useEffect(() => {
    if (editorRef) {
      editorRef.current = editor;
    }
  }, [editor, editorRef]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!locked);

      editor.setOptions({
        editorProps: {
          ...editor.options.editorProps,
          attributes: {
            ...(editor.options.editorProps.attributes as Record<string, string>),
            'aria-readonly': String(locked),
          },
        },
      });
    }
  }, [editor, locked]);

  useEffect(() => {
    const value = JSON.stringify(content);

    // Cell/popover focus is outside ProseMirror. Earlier queued saves must not replace the latest draft.
    if (
      editor &&
      value !== lastSent.current &&
      !editor.isFocused &&
      !workspace.snapshot().saving &&
      !workspace.snapshot().error
    ) {
      editor.commands.setContent(content, { emitUpdate: false });
      lastSent.current = value;
    }
  }, [content, editor]);

  const marks = useEditorState({
    editor,
    selector: ({ editor }) =>
      editor
        ? {
            bold: editor.isActive('bold'),
            italic: editor.isActive('italic'),
            underline: editor.isActive('underline'),
            strike: editor.isActive('strike'),
            code: editor.isActive('code'),
            link: editor.isActive('link'),
            kind: blockKindAt(editor),
            href: (editor.getAttributes('link').href as string | undefined) ?? '',
          }
        : null,
  });

  /**
   * Follows the caret to decide whether a command menu is open, and where. The
   * menu sits under the slash, inside the editor's own box, so it scrolls with
   * the note rather than floating over the wrong line.
   */
  function trackSlash(editor: Editor) {
    if (lockedRef.current) {
      return;
    }

    const range = slashRange(editor.state);

    if (!range || dismissedSlash.current === range.from) {
      if (!range) {
        dismissedSlash.current = null;
      }

      if (slashRef.current) {
        setSlash(null);
      }

      return;
    }

    const coords = editor.view.coordsAtPos(range.from),
      box = wrap.current?.getBoundingClientRect() ?? { left: 0, top: 0 };

    const next = {
      ...range,
      left: Math.round(coords.left - box.left),
      top: Math.round(coords.bottom - box.top + 6),
    };

    const current = slashRef.current;

    if (
      !current ||
      current.from !== next.from ||
      current.query !== next.query ||
      current.top !== next.top
    ) {
      if (!current || current.from !== next.from) {
        setSlashIndex(0);
      }

      setSlash(next);
    }
  }

  /**
   * Runs a menu command where it was typed. An empty block becomes the thing;
   * a block with words keeps them and the thing goes beneath, as in Notion.
   */
  function runCommand(command: BlockCommand, at: { from: number; to: number } | null) {
    if (!editor) {
      return;
    }

    if (at) {
      editor.chain().focus().deleteRange({ from: at.from, to: at.to }).run();
    }

    setSlash(null);

    const { $from } = editor.state.selection,
      empty = $from.parent.content.size === 0;

    if (command.id === 'image') {
      imageInput.current?.click();

      return;
    }

    if (command.id === 'divider') {
      editor.chain().focus().setHorizontalRule().run();

      return;
    }

    if (command.id === 'table') {
      editor
        .chain()
        .focus()
        .insertContent([
          { type: 'typedTable', attrs: { table: newTable() } },
          { type: 'paragraph' },
        ])
        .run();

      return;
    }

    if (!empty) {
      const after = $from.after();

      editor
        .chain()
        .focus()
        .insertContentAt(after, { type: 'paragraph' })
        .setTextSelection(after + 1)
        .run();
    }

    setBlockKind(editor, command.id);
  }

  async function insertImage(file: File) {
    if (lockedRef.current) {
      return;
    }

    if (!['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(file.type)) {
      setMessage('Choose a PNG, JPEG, WebP or GIF image.');

      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setMessage('Use an image smaller than 5 MB in this prototype.');

      return;
    }

    const reader = new FileReader();

    reader.onerror = () => setMessage('The image could not be read.');

    reader.onload = () => {
      if (lockedRef.current) {
        return;
      }

      editor
        ?.chain()
        .focus()
        .setImage({ src: String(reader.result), alt: file.name })
        .run();

      setMessage('Image added. It will be saved with this note for offline use.');
    };

    reader.readAsDataURL(file);
  }

  hrefRef.current = marks?.href ?? '';

  function openLink() {
    setLinkHref(hrefRef.current);
    setLinkOpen(true);
  }

  function applyLink() {
    if (!editor) {
      return;
    }

    const href = linkHref.trim();

    if (href) {
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    } else {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    }

    setLinkOpen(false);
  }

  /** A new line beside the hovered block, opened on the command menu as Notion's plus does. */
  function addBlock(above: boolean) {
    if (!editor || !handle.node) {
      return;
    }

    const pos = above ? handle.pos : handle.pos + handle.node.nodeSize;

    editor
      .chain()
      .insertContentAt(pos, { type: 'paragraph', content: [{ type: 'text', text: '/' }] })
      .setTextSelection(pos + 2)
      .focus()
      .run();
  }

  const copy = async (markdown: boolean) => {
    if (!handle.node) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        blockText(resolveImages(handle.node.toJSON(), workspace.snapshot().records), markdown),
      );

      setMessage(markdown ? 'Block copied as Markdown.' : 'Block copied as plain text.');
    } catch {
      setMessage('Clipboard access was denied. Select the text and copy it manually.');
    }

    setBlockMenu(false);
  };

  function withHandledBlock(action: (editor: Editor, node: Node, pos: number) => void) {
    return () => {
      if (editor && handle.node) {
        action(editor, handle.node, handle.pos);
      }

      setBlockMenu(false);
    };
  }

  if (!editor) {
    return null;
  }

  const options = slash ? matchCommands(slash.query) : [];

  return (
    <div className="editor-wrap" data-locked={locked} ref={wrap}>
      {!locked && (
        <DragHandle
          editor={editor}
          className="note-drag-handle"
          onNodeChange={({ node, pos }) => setHandle({ node, pos })}
          onElementDragStart={() =>
            setMessage('Move the block to the insertion line. Press Escape to cancel.')
          }
          onElementDragEnd={() => setMessage('')}
        >
          <IconButton
            size="compact"
            label="Add a block below"
            title="Click to add a block below. Alt+click to add one above."
            onClick={event => addBlock(event.altKey)}
          >
            <Plus aria-hidden="true" size={15} />
          </IconButton>
          <ControlPopover
            label="Block actions"
            size="compact"
            open={blockMenu}
            onOpenChange={setBlockMenu}
            trigger={
              <IconButton
                size="compact"
                label="Block actions"
                title="Drag to move this block. Click for its actions."
                draggable
              >
                <GripVertical aria-hidden="true" />
              </IconButton>
            }
          >
            <div className="note-block-menu">
              {handle.node?.isTextblock ||
              handle.node?.type.name === 'blockquote' ||
              ['bulletList', 'orderedList', 'taskList'].includes(handle.node?.type.name ?? '') ? (
                <>
                  <strong>Turn into</strong>
                  {turnIntoKinds.map(kind => (
                    <Button
                      key={kind.id}
                      size="compact"
                      variant="ghost"
                      className="ui-menu-item"
                      onClick={withHandledBlock((editor, _node, pos) => {
                        editor
                          .chain()
                          .setTextSelection(pos + 1)
                          .run();

                        setBlockKind(editor, kind.id);
                      })}
                    >
                      {kind.label}
                    </Button>
                  ))}
                </>
              ) : null}
              <strong>Block</strong>
              <Button
                size="compact"
                variant="ghost"
                className="ui-menu-item"
                onClick={withHandledBlock((editor, node, pos) =>
                  editor
                    .chain()
                    .insertContentAt(pos + node.nodeSize, node.toJSON())
                    .run(),
                )}
              >
                Duplicate
              </Button>
              <Button
                size="compact"
                variant="ghost"
                className="ui-menu-item"
                onClick={() => void copy(false)}
              >
                Copy as text
              </Button>
              <Button
                size="compact"
                variant="ghost"
                className="ui-menu-item"
                onClick={() => void copy(true)}
              >
                Copy as Markdown
              </Button>
              <Button
                size="compact"
                variant="ghost"
                className="ui-menu-item"
                onClick={withHandledBlock((editor, _node, pos) => {
                  const tr = moveBlock(editor.state, editor.state.doc.resolve(pos).index(), -1);

                  if (tr) {
                    editor.view.dispatch(tr);
                    setMessage('Block moved up.');
                  }
                })}
              >
                Move up
              </Button>
              <Button
                size="compact"
                variant="ghost"
                className="ui-menu-item"
                onClick={withHandledBlock((editor, _node, pos) => {
                  const tr = moveBlock(editor.state, editor.state.doc.resolve(pos).index(), 1);

                  if (tr) {
                    editor.view.dispatch(tr);
                    setMessage('Block moved down.');
                  }
                })}
              >
                Move down
              </Button>
              <Button
                size="compact"
                variant="danger"
                className="ui-menu-item"
                onClick={withHandledBlock((editor, node, pos) =>
                  editor
                    .chain()
                    .deleteRange({ from: pos, to: pos + node.nodeSize })
                    .run(),
                )}
              >
                Delete block
              </Button>
            </div>
          </ControlPopover>
        </DragHandle>
      )}
      {!locked && (
        <BubbleMenu
          editor={editor}
          className="note-bubble-menu"
          aria-label="Formatting"
          options={{ placement: 'top-start', offset: 8 }}
          shouldShow={({ editor, state }) =>
            !state.selection.empty &&
            !editor.isActive('codeBlock') &&
            !editor.isActive('typedTable') &&
            !editor.isActive('image')
          }
        >
          {linkOpen ? (
            <div className="note-bubble-link">
              <Input
                variant="inline"
                aria-label="Link address"
                placeholder="Paste a link…"
                value={linkHref}
                autoFocus
                onChange={e => setLinkHref(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyLink();
                  }

                  if (e.key === 'Escape') {
                    setLinkOpen(false);
                  }
                }}
              />
              <Button size="compact" variant="secondary" onClick={applyLink}>
                {linkHref.trim() ? 'Apply' : 'Remove'}
              </Button>
            </div>
          ) : (
            <>
              <Select
                aria-label="Turn into"
                value={marks?.kind ?? 'text'}
                onChange={e => setBlockKind(editor, e.target.value as BlockKind)}
              >
                {turnIntoKinds.map(kind => (
                  <option key={kind.id} value={kind.id}>
                    {kind.label}
                  </option>
                ))}
              </Select>
              <IconButton
                size="compact"
                label="Bold"
                title="Bold (⌘B)"
                aria-pressed={marks?.bold}
                onClick={() => editor.chain().focus().toggleBold().run()}
              >
                <Bold size={15} />
              </IconButton>
              <IconButton
                size="compact"
                label="Italic"
                title="Italic (⌘I)"
                aria-pressed={marks?.italic}
                onClick={() => editor.chain().focus().toggleItalic().run()}
              >
                <Italic size={15} />
              </IconButton>
              <IconButton
                size="compact"
                label="Underline"
                title="Underline (⌘U)"
                aria-pressed={marks?.underline}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
              >
                <Underline size={15} />
              </IconButton>
              <IconButton
                size="compact"
                label="Strikethrough"
                title="Strikethrough (⌘⇧S)"
                aria-pressed={marks?.strike}
                onClick={() => editor.chain().focus().toggleStrike().run()}
              >
                <Strikethrough size={15} />
              </IconButton>
              <IconButton
                size="compact"
                label="Code"
                title="Code (⌘E)"
                aria-pressed={marks?.code}
                onClick={() => editor.chain().focus().toggleCode().run()}
              >
                <Code size={15} />
              </IconButton>
              <IconButton
                size="compact"
                label="Link"
                title="Link (⌘K)"
                aria-pressed={marks?.link}
                onClick={openLink}
              >
                <Link2 size={15} />
              </IconButton>
            </>
          )}
        </BubbleMenu>
      )}
      <Input
        variant="inline"
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        ref={imageInput}
        hidden
        onChange={e => {
          if (e.target.files?.[0]) {
            void insertImage(e.target.files[0]);
          }

          e.target.value = '';
        }}
      />
      <EditorContent editor={editor} />
      {slash && options.length > 0 && (
        <div
          className="note-command-menu"
          role="listbox"
          aria-label="Block commands"
          style={{ left: slash.left, top: slash.top }}
        >
          {options.map((command, index) => {
            const Icon = kindIcons[command.id];

            return (
              <button
                key={command.id}
                type="button"
                data-ui
                role="option"
                aria-selected={index === slashIndex}
                className={index === slashIndex ? 'note-command is-active' : 'note-command'}
                onMouseDown={event => event.preventDefault()}
                onMouseEnter={() => setSlashIndex(index)}
                onClick={() => runCommand(command, slash)}
              >
                <span className="note-command-icon">
                  <Icon size={16} />
                </span>
                <span>
                  <strong>{command.label}</strong>
                  <small>{command.detail}</small>
                </span>
              </button>
            );
          })}
        </div>
      )}
      <span role="status" className="editor-message">
        {message}
      </span>
    </div>
  );
}

// Kept exported for the keyboard tests, which drive the same command the
// shortcut does without a view.
export { moveSelectedBlock };
